import { cs } from '../../lib/currency';
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import CreditBalanceBanner from '../../components/customer/CreditBalanceBanner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Loader2, Calendar, MapPin, DollarSign, Clock, Users,
  Tent, ChevronRight, CheckCircle2, AlertCircle, Baby, UserCheck,
  CreditCard, AlertTriangle, Info
} from 'lucide-react';
import api from '../../services/api';
import NetAmount from '../../components/shared/NetAmount';
import DescriptionView from '../../components/DescriptionView';
import { useBusinessTimezone, getTodayInTz, formatDateOnlyDisplay } from '../../utils/businessTime';
import { sanitizeHtml } from '../../utils/sanitize';

const EXPERIENCE_LEVELS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'competitive', label: 'Competitive' },
];

const ParentCamps = () => {
  const businessTz = useBusinessTimezone();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [activeCamps, setActiveCamps] = useState([]);
  const [myRegistrations, setMyRegistrations] = useState([]);
  const [children, setChildren] = useState([]);
  const [activeTab, setActiveTab] = useState('available');
  
  // Registration dialog state
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [registering, setRegistering] = useState(false);
  // Full-details dialog state (read-only, no form)
  const [detailsCamp, setDetailsCamp] = useState(null);
  
  // Registration form state
  const [formData, setFormData] = useState({
    player_id: '',
    player_name: '',
    player_dob: '',
    player_age: null,
    experience_level: '',
    medical_notes: '',
    additional_info: '',
    agreed_to_terms: false,
    selected_weeks: [],
    pay_deposit_only: false,
  });

  useEffect(() => {
    fetchData();
    
    // Handle payment callback
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId) {
      verifyPayment(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.error('Payment was cancelled');
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [campsRes, registrationsRes, childrenRes] = await Promise.all([
        api.get('/camps/active'),
        api.get('/camps/my-registrations'),
        api.get('/players/my-children')
      ]);
      
      setActiveCamps(campsRes.data || []);
      setMyRegistrations(registrationsRes.data || []);
      setChildren(childrenRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load camp information');
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (sessionId) => {
    try {
      const response = await api.get(`/camps/checkout/verify/${sessionId}`);
      if (response.data.status === 'success') {
        toast.success('Payment successful! Registration confirmed.');
        setActiveTab('registrations');
        fetchData();
      }
    } catch (error) {
      console.error('Payment verification error:', error);
    }
  };

  // Check if a child is already registered for a camp
  const isChildRegistered = (campId, playerId) => {
    return myRegistrations.some(
      reg => reg.camp_id === campId && 
             reg.player_id === playerId && 
             reg.status !== 'cancelled'
    );
  };

  // Check if camp is full
  const isCampFull = (camp) => {
    if (!camp.max_participants) return false;
    return (camp.registered_count || 0) >= camp.max_participants;
  };

  // Open registration dialog
  const openRegisterDialog = (camp) => {
    setSelectedCamp(camp);
    setFormData({
      player_id: '',
      player_name: '',
      player_dob: '',
      player_age: null,
      experience_level: '',
      medical_notes: '',
      additional_info: '',
      agreed_to_terms: false,
      selected_weeks: [],
      pay_deposit_only: camp.deposit_amount > 0,
    });
    setRegisterDialogOpen(true);
  };

  // Calculate age from DOB — uses business-TZ "today" so the cutoff
  // never drifts by a day for parents browsing from another TZ.
  const calculateAge = (dob) => {
    if (!dob) return null;
    const [by, bm, bd] = String(dob).split('-').map(Number);
    if (!by || !bm || !bd) return null;
    const [ty, tm, td] = getTodayInTz(businessTz).split('-').map(Number);
    let age = ty - by;
    if (tm < bm || (tm === bm && td < bd)) age--;
    return age;
  };

  // Handle child selection
  const handleChildSelect = (playerId) => {
    if (playerId === 'other') {
      setFormData({
        ...formData,
        player_id: '',
        player_name: '',
        player_dob: '',
        player_age: null,
      });
    } else {
      const child = children.find(c => c.id === playerId);
      if (child) {
        const age = calculateAge(child.date_of_birth);
        setFormData({
          ...formData,
          player_id: playerId,
          player_name: child.full_name,
          player_dob: child.date_of_birth || '',
          player_age: age,
          experience_level: child.skill_level || formData.experience_level,
        });
      }
    }
  };

  // Toggle week selection
  const toggleWeek = (weekNumber) => {
    setFormData(prev => ({
      ...prev,
      selected_weeks: prev.selected_weeks.includes(weekNumber)
        ? prev.selected_weeks.filter(w => w !== weekNumber)
        : [...prev.selected_weeks, weekNumber].sort((a, b) => a - b)
    }));
  };

  // Calculate total price with discount
  const calculatePrice = () => {
    if (!selectedCamp) return { total: 0, baseAmount: 0, discount: 0 };
    
    if (selectedCamp.weeks?.length > 0 && selectedCamp.price_per_week && formData.selected_weeks.length > 0) {
      const numWeeks = formData.selected_weeks.length;
      const baseAmount = selectedCamp.price_per_week * numWeeks;
      
      // Calculate discount if eligible
      const minWeeks = selectedCamp.min_weeks_for_discount || 0;
      const discountType = selectedCamp.multi_week_discount_type;
      const discountValue = selectedCamp.multi_week_discount_value || 0;
      
      let discount = 0;
      if (numWeeks >= minWeeks && discountType && discountValue > 0) {
        if (discountType === 'percentage') {
          discount = baseAmount * (discountValue / 100);
        } else if (discountType === 'fixed_per_week') {
          discount = discountValue * numWeeks;
        }
      }
      
      return { total: baseAmount - discount, baseAmount, discount };
    }
    
    return { total: selectedCamp.fee || 0, baseAmount: selectedCamp.fee || 0, discount: 0 };
  };

  // Check if discount is available
  const getDiscountInfo = () => {
    if (!selectedCamp) return null;
    
    const discountType = selectedCamp.multi_week_discount_type;
    const discountValue = selectedCamp.multi_week_discount_value;
    const minWeeks = selectedCamp.min_weeks_for_discount;
    
    if (!discountType || !discountValue || !minWeeks) return null;
    
    const discountText = discountType === 'percentage' 
      ? `${discountValue}% off` 
      : `${cs()}${discountValue} off per week`;
    
    return { discountText, minWeeks };
  };

  // Check eligibility
  const checkEligibility = () => {
    if (!selectedCamp || !formData.player_age) return { eligible: true, message: '' };
    
    const age = formData.player_age;
    const minAge = selectedCamp.min_age;
    const maxAge = selectedCamp.max_age;
    
    if (minAge && age < minAge) {
      return { eligible: false, message: `Player must be at least ${minAge} years old` };
    }
    if (maxAge && age > maxAge) {
      return { eligible: false, message: `Player must be ${maxAge} years old or younger` };
    }
    
    // Check experience level
    if (selectedCamp.experience_levels?.length > 0 && formData.experience_level) {
      if (!selectedCamp.experience_levels.includes(formData.experience_level)) {
        return { 
          eligible: false, 
          message: `Camp is only for ${selectedCamp.experience_levels.join(', ')} players` 
        };
      }
    }
    
    return { eligible: true, message: '' };
  };

  // Submit registration with Stripe checkout
  const handleRegister = async () => {
    if (!selectedCamp) return;
    
    const eligibility = checkEligibility();
    if (!eligibility.eligible) {
      toast.error(eligibility.message);
      return;
    }
    
    if (!formData.player_id && !formData.player_name) {
      toast.error('Please select a child or enter player details');
      return;
    }
    
    // For multi-week camps, require week selection
    if (selectedCamp.weeks?.length > 0 && formData.selected_weeks.length === 0) {
      toast.error('Please select at least one week');
      return;
    }
    
    if (selectedCamp.terms_and_conditions && !formData.agreed_to_terms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }
    
    setRegistering(true);
    try {
      const payload = {
        camp_id: selectedCamp.id,
        player_id: formData.player_id || null,
        player_name: formData.player_name || null,
        player_dob: formData.player_dob || null,
        player_age: formData.player_age,
        experience_level: formData.experience_level || null,
        medical_notes: formData.medical_notes || null,
        additional_info: formData.additional_info || null,
        agreed_to_terms: formData.agreed_to_terms,
        selected_weeks: formData.selected_weeks.length > 0 ? formData.selected_weeks : null,
        pay_deposit_only: formData.pay_deposit_only,
      };
      
      const response = await api.post('/camps/checkout', payload);
      
      if (response.data?.paid_via_credit) {
        toast.success(
          `Paid with account credit · ${cs()}${Number(response.data.credit_applied || response.data.amount || 0).toFixed(2)} deducted`,
        );
        setRegisterDialogOpen(false);
        fetchData();
      } else if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.success('Registration submitted successfully!');
        setRegisterDialogOpen(false);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register');
    } finally {
      setRegistering(false);
    }
  };

  // Pay remaining balance
  const payRemainingBalance = async (registrationId) => {
    try {
      const response = await api.post(`/camps/checkout/remaining/${registrationId}`);
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout');
    }
  };

  // Format date for display — assumes YYYY-MM-DD date-only strings
  // from the backend; routes through `formatDateOnlyDisplay` so the
  // rendered day never shifts because of browser timezone.
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return formatDateOnlyDisplay(dateStr, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '';
    return formatDateOnlyDisplay(dateStr, {
      month: 'short',
      day: 'numeric',
      year: undefined,
    });
  };

  if (loading) {
    return (
      <DashboardLayout title="Camps">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  const priceInfo = calculatePrice();
  const discountInfo = getDiscountInfo();
  const depositAmount = selectedCamp?.deposit_amount || 0;

  return (
    <DashboardLayout title="Camps">
      <div className="space-y-6" data-testid="parent-camps-page">
        {/* Header */}
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Tent className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" />
            Camps
          </h1>
          <p className="text-sm lg:text-base text-slate-600">Register your children for training camps</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="available" data-testid="available-camps-tab" className="text-xs lg:text-sm">
              <Tent className="h-4 w-4 lg:mr-2" />
              <span className="hidden lg:inline">Available Camps</span>
              <span className="lg:hidden">Available</span>
              <span className="ml-1">({activeCamps.length})</span>
            </TabsTrigger>
            <TabsTrigger value="registrations" data-testid="my-registrations-tab" className="text-xs lg:text-sm">
              <UserCheck className="h-4 w-4 lg:mr-2" />
              <span className="hidden lg:inline">My Registrations</span>
              <span className="lg:hidden">My Regs</span>
              <span className="ml-1">({myRegistrations.length})</span>
            </TabsTrigger>
          </TabsList>

          {/* Available Camps Tab */}
          <TabsContent value="available" className="mt-6">
            {activeCamps.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Tent className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-medium text-slate-900">No Camps Available</h3>
                  <p className="text-slate-500">Check back later for upcoming camps</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 lg:gap-4 grid-cols-1 md:grid-cols-2">
                {activeCamps.map(camp => {
                  const isFull = isCampFull(camp);
                  const spotsLeft = camp.max_participants 
                    ? camp.max_participants - (camp.registered_count || 0) 
                    : null;
                  const hasWeeks = camp.weeks?.length > 0;
                  
                  return (
                    <Card key={camp.id} className={isFull ? 'opacity-60' : ''}>
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-lg">{camp.name}</CardTitle>
                            <CardDescription className="mt-1 text-sm">
                              {hasWeeks ? `${camp.weeks.length} Week Camp` : 'Training Camp'}
                            </CardDescription>
                          </div>
                          <Badge 
                            variant={isFull ? 'destructive' : 'default'}
                            className={isFull ? '' : 'bg-green-500'}
                          >
                            {isFull ? 'Full' : 'Open'}
                          </Badge>
                        </div>
                      </CardHeader>
                      
                      <CardContent className="space-y-3">
                        <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4 text-blue-500" />
                            {formatDate(camp.start_date)} - {formatDate(camp.end_date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4 text-purple-500" />
                            {camp.start_time} - {camp.end_time}
                          </span>
                        </div>
                        
                        {camp.location && (
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <MapPin className="h-4 w-4 text-red-500" />
                            {camp.location}
                          </div>
                        )}
                        
                        {/* Week badges for multi-week camps */}
                        {hasWeeks && (
                          <div className="flex flex-wrap gap-1">
                            {camp.weeks.filter(w => w.is_available).map(week => (
                              <Badge key={week.week_number} variant="outline" className="text-xs">
                                W{week.week_number}: {formatShortDate(week.start_date)}
                              </Badge>
                            ))}
                          </div>
                        )}
                        
                        {!hasWeeks && camp.days_of_week?.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {camp.days_of_week.map(day => (
                              <Badge key={day} variant="outline" className="text-xs capitalize">
                                {day.slice(0, 3)}
                              </Badge>
                            ))}
                          </div>
                        )}
                        
                        {(camp.min_age || camp.max_age) && (
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <Baby className="h-4 w-4 text-pink-500" />
                            Ages: {camp.min_age || 'Any'} - {camp.max_age || 'Any'}
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between pt-2 border-t">
                          <div>
                            {hasWeeks && camp.price_per_week ? (
                              <>
                                <div className="flex items-center gap-1 text-lg font-bold text-green-600">
                                  <DollarSign className="h-5 w-5" />
                                  {camp.price_per_week.toFixed(2)}/week
                                </div>
                                {camp.multi_week_discount_type && camp.multi_week_discount_value > 0 && (
                                  <div className="text-xs text-purple-600 font-medium">
                                    {camp.multi_week_discount_type === 'percentage' 
                                      ? `${camp.multi_week_discount_value}% off` 
                                      : `${cs()}${camp.multi_week_discount_value} off/week`
                                    } for {camp.min_weeks_for_discount}+ weeks!
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="flex items-center gap-1 text-lg font-bold text-green-600">
                                <DollarSign className="h-5 w-5" />
                                {camp.fee.toFixed(2)}
                              </div>
                            )}
                            {camp.deposit_amount > 0 && (
                              <div className="text-xs text-amber-600">
                                {cs()}{camp.deposit_amount.toFixed(2)} deposit to reserve
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <Users className="h-4 w-4" />
                            {spotsLeft !== null 
                              ? `${spotsLeft} spots left` 
                              : `${camp.registered_count || 0} registered`
                            }
                          </div>
                        </div>
                        
                        {camp.description && (
                          <DescriptionView
                            value={camp.description}
                            className="text-sm text-slate-600 line-clamp-3"
                          />
                        )}
                      </CardContent>
                      
                      <CardFooter className="flex-col gap-2">
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => setDetailsCamp(camp)}
                          data-testid={`view-camp-details-${camp.id}`}
                        >
                          <Info className="h-4 w-4 mr-1" />
                          View Full Details
                        </Button>
                        <Button 
                          className="w-full"
                          disabled={isFull}
                          onClick={() => openRegisterDialog(camp)}
                          data-testid={`register-camp-${camp.id}`}
                        >
                          {isFull ? 'Camp is Full' : 'Register Now'}
                          {!isFull && <ChevronRight className="h-4 w-4 ml-1" />}
                        </Button>
                      </CardFooter>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* My Registrations Tab */}
          <TabsContent value="registrations" className="mt-6">
            {myRegistrations.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <UserCheck className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-medium text-slate-900">No Registrations Yet</h3>
                  <p className="text-slate-500 mb-4">Register your children for camps to see them here</p>
                  <Button onClick={() => setActiveTab('available')}>
                    Browse Available Camps
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {myRegistrations.map(reg => (
                  <Card key={reg.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h3 className="font-semibold">{reg.camp_name || 'Camp'}</h3>
                            <Badge variant={
                              reg.status === 'confirmed' ? 'default' :
                              reg.status === 'cancelled' ? 'destructive' : 'secondary'
                            }>
                              {reg.status}
                            </Badge>
                            <Badge variant={
                              reg.payment_status === 'paid' ? 'default' :
                              reg.payment_status === 'deposit_paid' ? 'outline' :
                              reg.payment_status === 'refunded' ? 'destructive' : 'secondary'
                            } className={
                              reg.payment_status === 'paid' ? 'bg-green-500' : 
                              reg.payment_status === 'deposit_paid' ? 'border-amber-500 text-amber-700' : ''
                            }>
                              {reg.payment_status === 'paid' ? 'Paid' : 
                               reg.payment_status === 'deposit_paid' ? 'Deposit Paid' :
                               reg.payment_status === 'pending' ? 'Payment Pending' : reg.payment_status}
                            </Badge>
                          </div>
                          
                          <div className="space-y-1 text-sm text-slate-600">
                            <p><span className="font-medium">Player:</span> {reg.player_name}</p>
                            {reg.player_age && <p><span className="font-medium">Age:</span> {reg.player_age} years</p>}
                            {reg.selected_weeks?.length > 0 && (
                              <p>
                                <span className="font-medium">Weeks:</span>{' '}
                                {reg.selected_weeks.map(w => `Week ${w}`).join(', ')}
                              </p>
                            )}
                            <p className="flex items-center gap-1.5"><span className="font-medium">Total Fee:</span>
                              <NetAmount
                                amount={reg.payment_amount}
                                refundedAmount={reg.refunded_amount}
                                refundStatus={reg.refund_status}
                                testId={`parent-camp-net-${reg.id}`}
                              />
                            </p>
                            {reg.deposit_amount > 0 && reg.deposit_paid && reg.payment_status !== 'paid' && (
                              <p className="text-amber-600">
                                <span className="font-medium">Deposit Paid:</span> {cs()}{reg.deposit_amount.toFixed(2)}
                                <span className="ml-2">|</span>
                                <span className="ml-2">Remaining: {cs()}{(reg.payment_amount - reg.deposit_amount).toFixed(2)}</span>
                              </p>
                            )}
                            <p><span className="font-medium">Registered:</span> {formatDate(reg.registered_at)}</p>
                          </div>
                        </div>
                        
                        <div className="text-right space-y-2">
                          {reg.status === 'confirmed' && reg.payment_status === 'paid' && (
                            <div className="flex items-center gap-1 text-green-600">
                              <CheckCircle2 className="h-5 w-5" />
                              Confirmed
                            </div>
                          )}
                          {reg.status === 'pending' && (
                            <div className="flex items-center gap-1 text-amber-600">
                              <AlertCircle className="h-5 w-5" />
                              Pending
                            </div>
                          )}
                          
                          {/* Pay remaining balance button */}
                          {reg.payment_status === 'deposit_paid' && reg.status !== 'cancelled' && (
                            <Button 
                              size="sm"
                              onClick={() => payRemainingBalance(reg.id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CreditCard className="h-4 w-4 mr-1" />
                              Pay Remaining
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Full Details Dialog (read-only) */}
        <Dialog open={!!detailsCamp} onOpenChange={(v) => !v && setDetailsCamp(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="camp-details-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Tent className="h-5 w-5 text-blue-600" />
                {detailsCamp?.name}
              </DialogTitle>
              {detailsCamp?.subtitle && (
                <DialogDescription>{detailsCamp.subtitle}</DialogDescription>
              )}
            </DialogHeader>
            {detailsCamp && (
              <div className="space-y-4">
                {/* Key facts row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-slate-700">
                    <Calendar className="h-4 w-4 text-blue-600 shrink-0" />
                    <span>{detailsCamp.start_date} — {detailsCamp.end_date}</span>
                  </div>
                  {(detailsCamp.start_time || detailsCamp.end_time) && (
                    <div className="flex items-center gap-2 text-slate-700">
                      <Clock className="h-4 w-4 text-purple-600 shrink-0" />
                      <span>{detailsCamp.start_time} – {detailsCamp.end_time}</span>
                    </div>
                  )}
                  {detailsCamp.location && (
                    <div className="flex items-center gap-2 text-slate-700 sm:col-span-2">
                      <MapPin className="h-4 w-4 text-rose-600 shrink-0" />
                      <span>{detailsCamp.location}</span>
                    </div>
                  )}
                  {(detailsCamp.min_age || detailsCamp.max_age) && (
                    <div className="flex items-center gap-2 text-slate-700">
                      <Baby className="h-4 w-4 text-pink-600 shrink-0" />
                      <span>Ages: {detailsCamp.min_age || 'Any'} – {detailsCamp.max_age || 'Any'}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-slate-700">
                    <DollarSign className="h-4 w-4 text-emerald-600 shrink-0" />
                    <span className="font-semibold text-emerald-700">
                      {cs()}{(detailsCamp.price_per_week || detailsCamp.price || 0).toFixed(2)}
                      {detailsCamp.weeks?.length > 0 ? ' / week' : ''}
                    </span>
                  </div>
                </div>

                {/* Weeks */}
                {detailsCamp.weeks?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Available weeks</p>
                    <div className="flex flex-wrap gap-1.5">
                      {detailsCamp.weeks.map((w, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          W{idx + 1}: {w.start_date || w.start || w}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Fine-print badges */}
                <div className="flex flex-wrap gap-2 text-xs">
                  {detailsCamp.multi_week_discount && (
                    <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                      {detailsCamp.multi_week_discount}% off for 3+ weeks
                    </Badge>
                  )}
                  {detailsCamp.deposit_amount > 0 && (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      {cs()}{detailsCamp.deposit_amount.toFixed(2)} deposit to reserve
                    </Badge>
                  )}
                </div>

                {/* Full description (markdown) */}
                {detailsCamp.description && (
                  <div className="border-t pt-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-2">About this camp</p>
                    <DescriptionView
                      value={detailsCamp.description}
                      className="text-sm text-slate-700"
                    />
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailsCamp(null)}>Close</Button>
              <Button
                disabled={!detailsCamp || (detailsCamp.max_capacity && detailsCamp.registered_count >= detailsCamp.max_capacity)}
                onClick={() => {
                  const c = detailsCamp;
                  setDetailsCamp(null);
                  openRegisterDialog(c);
                }}
                data-testid="camp-details-register-btn"
              >
                Register Now
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Registration Dialog */}
        <Dialog open={registerDialogOpen} onOpenChange={setRegisterDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Register for {selectedCamp?.name}</DialogTitle>
              <DialogDescription>
                {selectedCamp?.weeks?.length > 0 && selectedCamp?.price_per_week
                  ? `${cs()}${selectedCamp.price_per_week.toFixed(2)}/week`
                  : `${cs()}${selectedCamp?.fee?.toFixed(2)}`
                }
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Child Selection */}
              <div>
                <Label>Select Child *</Label>
                <Select
                  value={formData.player_id || 'other'}
                  onValueChange={handleChildSelect}
                >
                  <SelectTrigger data-testid="child-select">
                    <SelectValue placeholder="Select a child" />
                  </SelectTrigger>
                  <SelectContent>
                    {children.map(child => (
                      <SelectItem 
                        key={child.id} 
                        value={child.id}
                        disabled={isChildRegistered(selectedCamp?.id, child.id)}
                      >
                        {child.full_name} 
                        {child.date_of_birth && ` (${calculateAge(child.date_of_birth)} yrs)`}
                        {isChildRegistered(selectedCamp?.id, child.id) && ' - Already registered'}
                      </SelectItem>
                    ))}
                    <SelectItem value="other">Register someone else...</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Manual entry if "other" selected */}
              {!formData.player_id && (
                <>
                  <div>
                    <Label>Player Name *</Label>
                    <Input
                      value={formData.player_name}
                      onChange={(e) => setFormData({ ...formData, player_name: e.target.value })}
                      placeholder="Enter player's full name"
                      data-testid="player-name-input"
                    />
                  </div>
                  <div>
                    <Label>Date of Birth</Label>
                    <Input
                      type="date"
                      value={formData.player_dob}
                      onChange={(e) => {
                        const dob = e.target.value;
                        setFormData({ 
                          ...formData, 
                          player_dob: dob,
                          player_age: calculateAge(dob)
                        });
                      }}
                      data-testid="player-dob-input"
                    />
                  </div>
                </>
              )}
              
              {/* Age & Eligibility Display */}
              {formData.player_age && (
                <div className={`p-3 rounded-lg ${
                  checkEligibility().eligible 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <div className="flex items-center gap-2">
                    {checkEligibility().eligible ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-600" />
                    )}
                    <span className="font-medium">
                      Age: {formData.player_age} years
                      {checkEligibility().eligible 
                        ? ' - Eligible' 
                        : ` - ${checkEligibility().message}`
                      }
                    </span>
                  </div>
                </div>
              )}
              
              {/* Week Selection for Multi-Week Camps */}
              {selectedCamp?.weeks?.length > 0 && (
                <div>
                  <Label>Select Weeks *</Label>
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border rounded-lg p-3">
                    {selectedCamp.weeks.filter(w => w.is_available).map(week => (
                      <label 
                        key={week.week_number}
                        className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                          formData.selected_weeks.includes(week.week_number)
                            ? 'bg-blue-50 border border-blue-200'
                            : 'hover:bg-slate-50'
                        }`}
                      >
                        <Checkbox
                          checked={formData.selected_weeks.includes(week.week_number)}
                          onCheckedChange={() => toggleWeek(week.week_number)}
                        />
                        <div className="flex-1">
                          <span className="font-medium">Week {week.week_number}</span>
                          <span className="text-sm text-slate-500 ml-2">
                            {formatShortDate(week.start_date)} - {formatShortDate(week.end_date)}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-green-600">
                          {cs()}{selectedCamp.price_per_week?.toFixed(2)}
                        </span>
                      </label>
                    ))}
                  </div>
                  {formData.selected_weeks.length > 0 && (
                    <div className="mt-2 text-sm text-slate-600">
                      Selected: {formData.selected_weeks.length} week(s)
                    </div>
                  )}
                </div>
              )}
              
              {/* Experience Level */}
              {selectedCamp?.capture_experience && (
                <div>
                  <Label>Experience Level</Label>
                  <Select
                    value={formData.experience_level}
                    onValueChange={(value) => setFormData({ ...formData, experience_level: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select experience level" />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPERIENCE_LEVELS.map(level => (
                        <SelectItem key={level.value} value={level.value}>
                          {level.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Medical Notes */}
              {selectedCamp?.capture_medical_notes && (
                <div>
                  <Label>Medical Notes / Allergies</Label>
                  <Textarea
                    value={formData.medical_notes}
                    onChange={(e) => setFormData({ ...formData, medical_notes: e.target.value })}
                    placeholder="Any medical conditions or allergies we should know about"
                    rows={2}
                  />
                </div>
              )}
              
              {/* Additional Info */}
              {selectedCamp?.additional_info_label && (
                <div>
                  <Label>{selectedCamp.additional_info_label}</Label>
                  <Textarea
                    value={formData.additional_info}
                    onChange={(e) => setFormData({ ...formData, additional_info: e.target.value })}
                    rows={2}
                  />
                </div>
              )}
              
              {/* Terms and Conditions */}
              {selectedCamp?.terms_and_conditions && (
                <div className="space-y-2">
                  <Label>Terms & Conditions</Label>
                  <div 
                    className="p-3 bg-slate-50 rounded-lg text-sm prose prose-sm max-w-none max-h-32 overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedCamp.terms_and_conditions) }}
                  />
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.agreed_to_terms}
                      onCheckedChange={(checked) => setFormData({ ...formData, agreed_to_terms: checked })}
                      data-testid="terms-checkbox"
                    />
                    <span className="text-sm">I agree to the terms and conditions</span>
                  </label>
                </div>
              )}
              
              {/* Payment Summary */}
              <div className="border-t pt-4 space-y-3">
                {/* Show discount info */}
                {priceInfo.discount > 0 && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-600">Subtotal ({formData.selected_weeks.length} weeks):</span>
                      <span className="text-slate-600">{cs()}{priceInfo.baseAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm text-green-700 font-medium">
                      <span>Multi-week discount:</span>
                      <span>-{cs()}{priceInfo.discount.toFixed(2)}</span>
                    </div>
                  </div>
                )}
                
                {/* Show discount hint if not enough weeks selected */}
                {discountInfo && formData.selected_weeks.length > 0 && formData.selected_weeks.length < discountInfo.minWeeks && (
                  <div className="text-sm text-purple-600 bg-purple-50 p-2 rounded">
                    Select {discountInfo.minWeeks - formData.selected_weeks.length} more week(s) to get {discountInfo.discountText}!
                  </div>
                )}
                
                <div className="flex justify-between items-center">
                  <span className="font-medium">Total:</span>
                  <span className="text-xl font-bold text-green-600">{cs()}{priceInfo.total.toFixed(2)}</span>
                </div>
                
                {depositAmount > 0 && (
                  <>
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-amber-800">Non-refundable deposit: {cs()}{depositAmount.toFixed(2)}</p>
                          <p className="text-amber-700">Pay deposit now to reserve your spot, remaining balance due later.</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <label className={`flex-1 p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.pay_deposit_only ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                      }`}>
                        <input
                          type="radio"
                          name="payment_type"
                          checked={formData.pay_deposit_only}
                          onChange={() => setFormData({ ...formData, pay_deposit_only: true })}
                          className="sr-only"
                        />
                        <div className="text-center">
                          <div className="font-semibold">{cs()}{depositAmount.toFixed(2)}</div>
                          <div className="text-xs text-slate-500">Deposit Only</div>
                        </div>
                      </label>
                      <label className={`flex-1 p-3 border rounded-lg cursor-pointer transition-colors ${
                        !formData.pay_deposit_only ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                      }`}>
                        <input
                          type="radio"
                          name="payment_type"
                          checked={!formData.pay_deposit_only}
                          onChange={() => setFormData({ ...formData, pay_deposit_only: false })}
                          className="sr-only"
                        />
                        <div className="text-center">
                          <div className="font-semibold">{cs()}{priceInfo.total.toFixed(2)}</div>
                          <div className="text-xs text-slate-500">Pay in Full</div>
                        </div>
                      </label>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            <CreditBalanceBanner
              amount={formData.pay_deposit_only && depositAmount > 0 ? depositAmount : priceInfo.total}
              className="mx-6 mb-2"
            />

            <DialogFooter>
              <Button variant="outline" onClick={() => setRegisterDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleRegister} 
                disabled={registering || !checkEligibility().eligible}
                data-testid="submit-registration-btn"
              >
                {registering && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <CreditCard className="h-4 w-4 mr-2" />
                Pay {cs()}{formData.pay_deposit_only && depositAmount > 0 ? depositAmount.toFixed(2) : priceInfo.total.toFixed(2)}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ParentCamps;
